import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { cn } from "~/utils/helpers"

const OPEN_DELAY = 250
// Hovering another interactive cell while a card is open.
const SWITCH_DELAY = 150
const CLOSE_DELAY = 300
const MAX_HEIGHT = 440
const VIEWPORT_GAP = 8
const SIDE_OFFSET = 6
// `w-96`, the card's width.
const WIDTH = 384

/** The column's `cellClass`: globals.css lets the cell's content span the whole cell. */
export const INTERACTIVE_CELL_CLASS = "interactive-cell"

// The row tint while its card is open; styled with the inspector highlight in globals.css.
const OPEN_ROW_CLASS = "ag-row-interactive-cell-open"

const AT_REST =
  "[.ag-cell:hover_&]:hidden [.ag-cell-focus_&]:hidden group-focus-visible/interactive-cell:hidden group-data-[state=open]/interactive-cell:hidden"
const ACTIVE =
  "hidden [.ag-cell:hover_&]:inline [.ag-cell-focus_&]:inline group-focus-visible/interactive-cell:inline group-data-[state=open]/interactive-cell:inline"
const UNDERLINE =
  "underline decoration-dotted decoration-fg-muted [text-decoration-thickness:10%] underline-offset-2"

// Like HoverCardContent minus padding (the card content owns it) and its border: shadow-flyout
// already draws the 1px edge.
const SURFACE = "paragraph-sm text-fg-base"

/**
 * One card open at a time across the grid: opening a card closes the previous one at once, and
 * while one is open the next opens after SWITCH_DELAY instead of OPEN_DELAY.
 */
const openCard = (() => {
  let active: { id: string; close: () => void } | null = null
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach((listener) => listener())
  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    isAnyOpen: () => active != null,
    activate: (id: string, close: () => void) => {
      if (active && active.id !== id) active.close()
      active = { id, close }
      emit()
    },
    release: (id: string) => {
      if (active?.id !== id) return
      active = null
      emit()
    },
  }
})()

type Placement = {
  side: "left" | "right"
  alignOffset: number
  /** From the trigger, which sits inside the cell; leaves SIDE_OFFSET between card and cell. */
  sideOffset: number
  /** Room from the card's top to the viewport bottom, capped: expanding grows the card into it. */
  maxHeight: number
}

/**
 * Picked once per open and held: beside the cell (left, or right when the left has no room), top
 * level with the row, shifted up only as far as the card's default state needs to fit.
 */
function choosePlacement(
  anchor: DOMRect,
  cell: DOMRect,
  rowTop: number,
  defaultHeight: number,
  viewport: { width: number; height: number },
): Placement {
  const fitHeight = Math.min(defaultHeight, MAX_HEIGHT, viewport.height - 2 * VIEWPORT_GAP)
  const top = Math.max(
    VIEWPORT_GAP,
    Math.min(rowTop - VIEWPORT_GAP, viewport.height - VIEWPORT_GAP - fitHeight),
  )
  const maxHeight = Math.min(MAX_HEIGHT, viewport.height - VIEWPORT_GAP - top)
  const fitsLeft = cell.left - SIDE_OFFSET - WIDTH >= VIEWPORT_GAP
  return {
    side: fitsLeft ? "left" : "right",
    alignOffset: top - anchor.top,
    sideOffset: SIDE_OFFSET + (fitsLeft ? anchor.left - cell.left : cell.right - anchor.right),
    maxHeight,
  }
}

/**
 * The scrolling part of an InteractiveCell card, below whatever the card pins above it. A collapse
 * near the end of a scrolled body would clamp `scrollTop` and drag everything down, so the spacer
 * backfills exactly that shortfall (never when the body sits at the top) and shrinks again as the
 * reader scrolls up.
 */
export function InteractiveCellCardBody({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const spacerRef = useRef<HTMLDivElement>(null)
  const contentHeight = useRef(0)
  const scrollTop = useRef(0)
  // Read just before a click or key toggles a section, while the old layout still stands, and held
  // until the reader scrolls: the card's height follows its content, so the body's clientHeight
  // shrinks frame by frame during a collapse and cannot be read afterwards.
  const beforeToggle = useRef<{ scrollTop: number; clientHeight: number } | null>(null)

  const sync = useCallback(() => {
    const scroller = scrollRef.current
    const content = contentRef.current
    const spacer = spacerRef.current
    if (!scroller || !content || !spacer) return
    const height = content.offsetHeight
    const hold = beforeToggle.current ?? (height !== contentHeight.current ? null : undefined)
    contentHeight.current = height
    if (hold !== undefined) {
      // A toggle is settling, or the content resized on its own: keep the position. A scroll event
      // in this state is the browser clamping, not the reader.
      const top = hold?.scrollTop ?? scrollTop.current
      const clientHeight = hold?.clientHeight ?? scroller.clientHeight
      const backfill = top > 0 ? Math.max(0, top + clientHeight - height) : 0
      spacer.style.height = `${backfill}px`
      scroller.scrollTop = top
      scrollTop.current = top
      return
    }
    scrollTop.current = scroller.scrollTop
    const needed =
      scroller.scrollTop > 0 ? Math.max(0, scroller.scrollTop + scroller.clientHeight - height) : 0
    if (needed < spacer.offsetHeight) spacer.style.height = `${needed}px`
  }, [])

  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content) return
    const observer = new ResizeObserver(sync)
    observer.observe(content)
    return () => observer.disconnect()
  }, [sync])

  const recordBeforeToggle = () => {
    const scroller = scrollRef.current
    if (!scroller) return
    beforeToggle.current = { scrollTop: scroller.scrollTop, clientHeight: scroller.clientHeight }
  }
  // The reader scrolling ends the hold.
  const release = () => {
    beforeToggle.current = null
  }

  return (
    <div
      ref={scrollRef}
      onScroll={sync}
      onWheel={release}
      onTouchMove={release}
      onPointerDownCapture={(e) => {
        // On the scroller itself: its scrollbar.
        if (e.target === e.currentTarget) release()
        else recordBeforeToggle()
      }}
      onKeyDownCapture={(e) => {
        if (e.key === "Enter" || e.key === " ") recordBeforeToggle()
        else release()
      }}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none]"
    >
      {/* flow-root: a last child's margin would otherwise escape offsetHeight but not scrollHeight. */}
      <div ref={contentRef} className="flow-root">
        {children}
      </div>
      <div ref={spacerRef} aria-hidden />
    </div>
  )
}

/** Renders the card off screen in its default state and reports its height. */
function DefaultHeightProbe({
  children,
  onMeasure,
}: {
  children: ReactNode
  onMeasure: (height: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  // useEffect, not useLayoutEffect: some content finishes its layout in its own effects (MiniTable
  // sets its grid columns there), and child effects run before this one.
  useEffect(() => {
    if (ref.current) onMeasure(ref.current.offsetHeight)
  }, [onMeasure])
  return createPortal(
    <div
      ref={ref}
      aria-hidden
      inert
      className={cn(
        SURFACE,
        "pointer-events-none invisible fixed top-0 left-0 flex w-96 -translate-x-[200vw] flex-col",
      )}
    >
      {children}
    </div>,
    document.body,
  )
}

/** Tints every element of the anchor's row (pinned containers render their own `.ag-row`). */
function useRowHighlight(anchorRef: React.RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    const row = anchorRef.current?.closest(".ag-row")
    const rowIndex = row?.getAttribute("row-index")
    const root = row?.closest(".ag-root")
    if (!active || !root || rowIndex == null) return
    const rows = [...root.querySelectorAll(`.ag-row[row-index="${rowIndex}"]`)]
    rows.forEach((r) => r.classList.add(OPEN_ROW_CLASS))
    return () => rows.forEach((r) => r.classList.remove(OPEN_ROW_CLASS))
  }, [anchorRef, active])
}

const isTouchOnly = () => window.matchMedia("(hover: none)").matches

/**
 * Keeps the wheel from scrolling the grid under the card: that would carry the row, and the card
 * anchored to it, out from under the cursor. The body still scrolls where it has room to.
 */
function useContainWheel() {
  const cleanup = useRef<(() => void) | null>(null)
  return useCallback((surface: HTMLElement | null) => {
    cleanup.current?.()
    cleanup.current = null
    if (!surface) return
    const onWheel = (e: WheelEvent) => {
      const body = (e.target as Element).closest?.(".overflow-y-auto")
      const canScroll =
        body &&
        (e.deltaY < 0
          ? body.scrollTop > 0
          : body.scrollTop + body.clientHeight < body.scrollHeight - 0.5)
      if (!canScroll) e.preventDefault()
    }
    surface.addEventListener("wheel", onWheel, { passive: false })
    cleanup.current = () => surface.removeEventListener("wheel", onWheel)
  }, [])
}

/** Opens the card when AG Grid's keyboard navigation focuses the `.ag-cell` (not the trigger). */
function useCellFocus(
  anchorRef: React.RefObject<HTMLElement | null>,
  onOpenChange: (open: boolean) => void,
  openedByCellFocus: React.RefObject<boolean>,
) {
  const onOpenChangeRef = useRef(onOpenChange)
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange
  })
  useEffect(() => {
    const cell = anchorRef.current?.closest<HTMLElement>(".ag-cell")
    if (!cell) return
    let timer = 0
    const onFocus = (e: FocusEvent) => {
      if (e.target !== cell) return
      window.clearTimeout(timer)
      timer = window.setTimeout(
        () => {
          openedByCellFocus.current = true
          onOpenChangeRef.current(true)
        },
        openCard.isAnyOpen() ? SWITCH_DELAY : OPEN_DELAY,
      )
    }
    const onBlur = (e: FocusEvent) => {
      if (e.target !== cell) return
      window.clearTimeout(timer)
      if (!openedByCellFocus.current || cell.contains(e.relatedTarget as Node | null)) return
      timer = window.setTimeout(() => onOpenChangeRef.current(false), CLOSE_DELAY)
    }
    cell.addEventListener("focus", onFocus)
    cell.addEventListener("blur", onBlur)
    return () => {
      window.clearTimeout(timer)
      cell.removeEventListener("focus", onFocus)
      cell.removeEventListener("blur", onBlur)
    }
  }, [anchorRef, openedByCellFocus])
}

type Props = {
  /** Leading icon, shown only with a label. */
  icon?: ReactNode
  /** The cell's value, underlined. Omit for the empty state. */
  label?: ReactNode
  /** Empty state: "-" at rest; this label while the cell is hovered, focused or open. Omit to keep "-". */
  emptyLabel?: string
  /**
   * The card's content, a flex column 384px wide (`w-96`). Rendered twice per open: once off screen
   * to measure its default height, then in the card. Put the part that scrolls in an
   * `InteractiveCellCardBody`.
   */
  card: ReactNode
}

/**
 * Purpleheart "Interactive cell" for AG Grid: a value that opens a hover card with its detail.
 * Figma: https://www.figma.com/design/IBrAOoIn8JljA34ly4RcXN/?node-id=3145-2558
 *
 * The whole cell is the hover/focus target; the container hugs its content and carries the states
 * (background on hover and while open, focus ring on keyboard focus). The card opens after 250ms
 * (150ms when switching from another open card), closes 300ms after the pointer leaves, and stays
 * open while the pointer crosses to it or over the room it may grow into. It sits beside the cell
 * (left, else right), top level with the row, shifted up only as far as its default state needs;
 * expanding grows it down to the viewport, then its `InteractiveCellCardBody` scrolls. Focus opens
 * it, Esc closes it, a tap toggles it on touch screens, and the row is tinted while it is open.
 *
 * The column must set `cellClass` to include `INTERACTIVE_CELL_CLASS`. Listed under
 * design-system/APPROVED.md § Hover surfaces, not yet promoted.
 */
export function InteractiveCell({ icon, label, emptyLabel, card }: Props) {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const openedByCellFocus = useRef(false)
  const anyOpen = useSyncExternalStore(openCard.subscribe, openCard.isAnyOpen)
  const openDelay = anyOpen && !open ? SWITCH_DELAY : OPEN_DELAY

  const close = useCallback(() => {
    setOpen(false)
    setPlacement(null)
    openedByCellFocus.current = false
    openCard.release(id)
  }, [id])

  const onOpenChange = (next: boolean) => {
    if (!next) return close()
    openCard.activate(id, close)
    setOpen(true)
  }

  useCellFocus(anchorRef, onOpenChange, openedByCellFocus)
  useEffect(() => () => openCard.release(id), [id])

  const measured = useCallback((defaultHeight: number) => {
    const anchor = anchorRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const cell = anchor.closest(".ag-cell")?.getBoundingClientRect() ?? rect
    const rowTop = anchor.closest(".ag-row")?.getBoundingClientRect().top ?? rect.top
    setPlacement(
      choosePlacement(rect, cell, rowTop, defaultHeight, {
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    )
  }, [])

  const isOpen = open && placement != null
  useRowHighlight(anchorRef, isOpen)
  const containWheel = useContainWheel()

  // The room the card may grow into, plus the gap to the cell, count as the card:
  // crossing them never reads as leaving it.
  const hitArea: CSSProperties | undefined = placement
    ? {
        top: 0,
        height: placement.maxHeight,
        width: WIDTH + placement.sideOffset,
        ...(placement.side === "left" ? { left: 0 } : { right: 0 }),
      }
    : undefined

  return (
    <HoverCard
      open={isOpen}
      onOpenChange={onOpenChange}
      openDelay={openDelay}
      closeDelay={CLOSE_DELAY}
    >
      <HoverCardTrigger asChild>
        <div
          ref={anchorRef}
          tabIndex={0}
          onClick={() => {
            if (isTouchOnly()) onOpenChange(!open)
          }}
          // −5px: the container's 4px padding plus the 1px cell border AG Grid also subtracts.
          className="group/interactive-cell flex min-w-0 flex-1 items-center self-stretch px-[calc(var(--ag-cell-horizontal-padding)-5px)] outline-none"
        >
          <span
            className={cn(
              "inline-flex min-w-0 max-w-full items-center gap-1 rounded px-1 py-0.5",
              "[.ag-cell:hover_&]:bg-component-pressed group-data-[state=open]/interactive-cell:bg-component-pressed",
              // AG Grid's keyboard navigation focuses the `.ag-cell`; Tab can focus this element.
              "group-focus-visible/interactive-cell:shadow-borders-focus [.ag-cell:focus-visible_&]:shadow-borders-focus",
            )}
          >
            {label != null ? (
              <>
                {icon}
                <span className={cn("truncate paragraph-sm text-fg-base", UNDERLINE)}>{label}</span>
              </>
            ) : (
              <span className="paragraph-sm text-fg-muted">
                <span className={emptyLabel ? AT_REST : undefined}>-</span>
                {emptyLabel && <span className={cn(ACTIVE, UNDERLINE)}>{emptyLabel}</span>}
              </span>
            )}
          </span>
        </div>
      </HoverCardTrigger>
      {open && placement == null && (
        <DefaultHeightProbe onMeasure={measured}>{card}</DefaultHeightProbe>
      )}
      {placement && (
        <HoverCardContent
          ref={containWheel}
          side={placement.side}
          align="start"
          alignOffset={placement.alignOffset}
          sideOffset={placement.sideOffset}
          // Placed once on open; re-flipping would pull the card out from under the cursor.
          avoidCollisions={false}
          className="relative w-96 overflow-visible rounded-none border-0 bg-transparent p-0 shadow-none"
          onPointerDownOutside={(e) => {
            // A tap on the cell toggles it.
            if (anchorRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
        >
          <div aria-hidden className="absolute" style={hitArea} />
          <div
            className={cn(
              SURFACE,
              "relative flex flex-col overflow-hidden rounded-[12px] bg-base shadow-flyout",
            )}
            style={{ maxHeight: placement.maxHeight }}
          >
            {card}
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  )
}
